import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';

const alphabet = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

export function tronAddressAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> => {
        const raw = control.value as string;
        const value = typeof raw === 'string' ? raw.trim() : raw;
        const isSecure = window.location.href.startsWith('https://');

        if (!value) {
            return Promise.resolve(null);
        }

        if (!isSecure) {
            const lengthCondition = value.length >= 30 && value.length <= 35;

            return Promise.resolve(value.startsWith('T') && lengthCondition ? null : { invalidAddress: true });
        }

        return validateTronAddress(value).then((isValid) => {
            return isValid ? null : { invalidAddress: true };
        });
    };
}

function base58Decode(str: string): Uint8Array | null {
    let num = BigInt(0);
    const alphabetMap: Record<string, number> = {};

    for (let i = 0; i < alphabet.length; i++) {
        alphabetMap[alphabet[i]] = i;
    }

    for (const char of str) {
        const value = alphabetMap[char];
        if (value === undefined) return null;
        num = num * BigInt(58) + BigInt(value);
    }

    const bytes: number[] = [];
    while (num > 0) {
        bytes.push(Number(num % BigInt(256)));
        num = num / BigInt(256);
    }
    bytes.reverse();

    let leadingZeroCount = 0;
    for (const char of str) {
        if (char === '1') leadingZeroCount++;
        else break;
    }

    const result = new Uint8Array(leadingZeroCount + bytes.length);
    result.set(bytes, leadingZeroCount);

    return result;
}

function sha256(buffer: Uint8Array): Promise<Uint8Array> {
    if (!crypto?.subtle) {
        return Promise.resolve(buffer);
    }

    return crypto?.subtle?.digest('SHA-256', buffer)?.then((hash) => new Uint8Array(hash));
}

async function validateTronAddress(address: string): Promise<boolean> {
    if (!/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address)) {
        return false;
    }

    const decoded = base58Decode(address);
    if (!decoded || decoded.length !== 25) return false;

    const body = decoded.slice(0, 21);
    const checksum = decoded.slice(21);

    if (body[0] !== 0x41) return false;

    const hash1 = await sha256(body);
    const hash2 = await sha256(hash1);
    const calcChecksum = hash2.slice(0, 4);

    for (let i = 0; i < 4; i++) {
        if (checksum[i] !== calcChecksum[i]) return false;
    }

    return true;
}
